import { useEffect, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import NotificationPreferencesSection from "../components/NotificationPreferencesSection";
import api, { formatApiError } from "../lib/api";
import { authenticatedDownload } from "../lib/pdf";
import { useAuth } from "../context/AuthContext";
import { Building2, FileText, Download, Landmark, Copy, Wrench, Plus, Loader2, CheckCircle2, Camera, X, Folder, FolderOpen, ChevronRight, FileSpreadsheet, Receipt, IdCard, Mail, Shield, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import WorkOrderPhotos from "../components/WorkOrderPhotos";
import { fmtUSDate } from "../lib/date";

export default function TenantDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [reportOpen, setReportOpen] = useState(null); // null | { lease }

  const load = async () => {
    const { data } = await api.get("/tenant/dashboard");
    setData(data);
    try {
      const d = await api.get(`/tenants/${user.id}/documents`);
      setDocs(d.data);
    } catch (error) {
      console.error("Failed to load tenant documents", error);
    }
    try {
      const wo = await api.get("/tenant/maintenance");
      setWorkOrders(wo.data.items || []);
    } catch (error) {
      console.error("Failed to load tenant maintenance", error);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadDoc = async (d) => {
    try {
      await authenticatedDownload(
        `/api/tenants/${user.id}/documents/${d.id}/download`,
        d.original_filename || d.label || "document",
      );
    } catch (error) {
      console.error("Document download failed", error);
      toast.error("Download failed");
    }
  };

  const payRent = async (lease_id) => {
    setBusy(lease_id);
    try {
      const { data } = await api.post("/checkout/rent", { lease_id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Payment failed");
      setBusy(null);
    }
  };

  if (!data) return <DashboardShell><div className="p-4 sm:p-8 text-slate-500">Loading…</div></DashboardShell>;

  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 md:p-8">
        <div className="overline text-slate-500">Tenant Portal</div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"Outfit"}}>Your leases</h1>

        <div className="mt-6 grid gap-4">
          {data.leases.length === 0 && (
            <div className="bg-white border border-slate-200 rounded-sm p-10 text-center text-slate-500" data-testid="no-leases">
              You don't have any active leases yet. Please contact your property manager.
            </div>
          )}
          {data.leases.map((l) => (
            <div key={l.id} className={`bg-white border rounded-sm p-6 ${l.accept_rent_payments === false ? "border-amber-200" : "border-slate-200"}`} data-testid={`tenant-lease-${l.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex gap-4 items-start">
                  <div className="w-11 h-11 bg-slate-900 text-white flex items-center justify-center rounded-sm"><Building2 className="w-5 h-5" /></div>
                  <div>
                    <div className="font-bold text-slate-900 text-lg" style={{fontFamily:"Outfit"}}>{l.property?.name}</div>
                    <div className="text-sm text-slate-500">{l.property?.address} · {l.unit_label}</div>
                    <div className="text-xs text-slate-400 font-mono mt-1">Lease {l.start_date} → {l.end_date}</div>
                  </div>
                </div>
                <div className="flex items-center gap-5">
                  <div>
                    <div className="overline text-slate-500">Monthly Rent</div>
                    <div className="text-2xl font-mono font-bold text-slate-900">${l.monthly_rent.toLocaleString()}</div>
                  </div>
                  <button
                    onClick={() => payRent(l.id)}
                    disabled={busy === l.id || l.accept_rent_payments === false}
                    data-testid={`pay-rent-${l.id}`}
                    className="btn-primary-dark disabled:opacity-60"
                    title={l.accept_rent_payments === false ? "Your investor has disabled online rent payments." : undefined}
                  >
                    {busy === l.id
                      ? "Redirecting…"
                      : l.accept_rent_payments === false
                        ? "Pay rent (disabled)"
                        : "Pay rent →"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setReportOpen({ lease: l })}
                    className="btn-outline-dark text-sm flex items-center gap-1.5"
                    data-testid={`report-issue-${l.id}`}
                  >
                    <Wrench className="w-4 h-4" /> Report issue
                  </button>
                </div>
              </div>
              {l.accept_rent_payments === false && (
                <div className="mt-3 text-xs font-mono uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2" data-testid={`pay-rent-disabled-${l.id}`}>
                  ⚠ Your investor has temporarily disabled online rent payments. Please pay by another method and contact them directly.
                </div>
              )}
              {l.bank_payment && <BankPaymentPanel bank={l.bank_payment} leaseId={l.id} rent={l.monthly_rent} />}
            </div>
          ))}
        </div>

        <div className="mt-10">
          <div className="overline text-slate-500 mb-3">Payment History</div>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            <table className="w-full text-sm min-w-[480px]" data-testid="payment-history-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <Th>Date</Th><Th>Reference</Th><Th>Status</Th><Th className="text-right">Amount</Th>
                </tr>
              </thead>
              <tbody>
                {data.payments.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-slate-400">No payments yet.</td></tr>}
                {data.payments.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100" data-testid={`payment-${p.id}`}>
                    <Td className="font-mono text-xs">{fmtUSDate(p.paid_at || p.created_at)}</Td>
                    <Td className="font-mono text-xs text-slate-500">{p.session_id.slice(0,24)}…</Td>
                    <Td>
                      <span className={`overline ${p.payment_status === "paid" ? "text-green-600" : "text-amber-600"}`}>
                        {p.payment_status}
                      </span>
                    </Td>
                    <Td className="text-right font-mono font-bold">${p.amount.toLocaleString()}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-10">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="overline text-slate-500">My Documents</div>
            <TenantUploadButton onUploaded={load} />
          </div>
          <DocumentFolders docs={docs} onDownload={downloadDoc} />
        </div>

        <MaintenanceSection
          workOrders={workOrders}
          leases={data.leases}
          onOpenReport={(lease) => setReportOpen({ lease })}
        />

        <NotificationPreferencesSection />
      </div>

      {reportOpen && (
        <ReportIssueDialog
          lease={reportOpen.lease}
          onClose={() => setReportOpen(null)}
          onSaved={() => {
            setReportOpen(null);
            load();
          }}
        />
      )}
    </DashboardShell>
  );
}

function Th({ children, className="" }) { return <th className={`px-5 py-3 overline text-slate-600 ${className}`}>{children}</th>; }
function Td({ children, className="" }) { return <td className={`px-5 py-4 ${className}`}>{children}</td>; }

function BankPaymentPanel({ bank, leaseId, rent }) {
  const [open, setOpen] = useState(false);
  const copy = async (label, value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      toast.success(`${label} copied`);
    } catch {
      toast.error("Couldn't copy — please select and copy manually.");
    }
  };
  const hasDetails = bank.routing_number || bank.account_last4;
  return (
    <div className="mt-3 border border-emerald-200 bg-emerald-50/60 rounded-sm overflow-hidden" data-testid={`bank-payment-panel-${leaseId}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-emerald-50 transition-colors text-left"
        data-testid={`bank-payment-toggle-${leaseId}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 bg-emerald-700 text-white flex items-center justify-center rounded-sm shrink-0">
            <Landmark className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-emerald-900 flex items-center gap-2" style={{fontFamily:"Outfit"}}>
              Pay by wire / ACH
              <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 bg-white border border-emerald-200 px-1.5 py-0.5 rounded-sm">No fees</span>
            </div>
            <div className="text-xs text-emerald-800 truncate">
              {bank.bank_name || "Your investor's bank"}{bank.account_last4 ? ` · ending ••••${bank.account_last4}` : ""}
            </div>
          </div>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 shrink-0">{open ? "Hide −" : "Show details +"}</span>
      </button>

      {open && (
        <div className="border-t border-emerald-200 px-4 py-4 bg-white" data-testid={`bank-payment-details-${leaseId}`}>
          {hasDetails ? (
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <CopyRow label="Account holder" value={bank.account_holder_name} onCopy={copy} />
              <CopyRow label="Bank" value={bank.bank_name} onCopy={copy} />
              <CopyRow label="Routing (ABA)" value={bank.routing_number} mono onCopy={copy} />
              <CopyRow
                label="Account"
                value={bank.account_last4 ? `••••${bank.account_last4}` : null}
                hint={bank.account_type ? bank.account_type : null}
                mono
                note="Ask your investor directly for the full account number."
              />
              <CopyRow label="Amount due" value={`$${Number(rent || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`} mono onCopy={copy} />
            </div>
          ) : (
            <div className="text-sm text-slate-600">Your investor hasn't published full deposit instructions yet. Please reach out to them directly.</div>
          )}
          {bank.instructions && (
            <div className="mt-4 text-sm bg-slate-50 border border-slate-200 rounded-sm p-3 whitespace-pre-wrap text-slate-700" data-testid={`bank-payment-instructions-${leaseId}`}>
              <div className="overline text-slate-500 mb-1">Instructions from your investor</div>
              {bank.instructions}
            </div>
          )}
          <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
            ⓘ ACH/wire transfers typically settle in 1–3 business days. Always include your name + unit in the memo line so your investor can reconcile the payment.
          </div>
        </div>
      )}
    </div>
  );
}

function CopyRow({ label, value, hint, note, mono, onCopy }) {
  if (!value) return null;
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 flex items-center justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="overline text-slate-500">{label}</div>
        <div className={`text-sm ${mono ? "font-mono" : ""} text-slate-900 truncate`}>
          {value}{hint ? <span className="text-slate-400 text-xs ml-1">· {hint}</span> : null}
        </div>
        {note && <div className="text-[10px] text-slate-500 mt-0.5">{note}</div>}
      </div>
      {onCopy && (
        <button
          type="button"
          onClick={() => onCopy(label, value)}
          className="text-slate-400 hover:text-slate-900 p-1.5 shrink-0"
          title={`Copy ${label.toLowerCase()}`}
          data-testid={`bank-copy-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          <Copy className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function MaintenanceSection({ workOrders, leases, onOpenReport }) {
  const statusMap = {
    open: { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "Open" },
    in_progress: { cls: "bg-sky-50 text-sky-700 border-sky-200", label: "In progress" },
    completed: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Completed" },
    cancelled: { cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Cancelled" },
  };
  const priorityClr = { low: "text-emerald-700", medium: "text-amber-700", high: "text-red-700", emergency: "text-rose-900 font-bold" };
  return (
    <div className="mt-10" data-testid="tenant-maintenance-section">
      <div className="flex items-center justify-between mb-3">
        <div className="overline text-slate-500">Maintenance requests</div>
        {leases.length > 0 && (
          <button
            onClick={() => onOpenReport(leases[0])}
            className="btn-primary-dark text-xs flex items-center gap-1.5"
            data-testid="tenant-report-issue-cta"
          >
            <Plus className="w-3.5 h-3.5" /> Report a new issue
          </button>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-sm">
        {workOrders.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="tenant-maintenance-empty">
            You haven't reported any issues yet. Use the button above (or "Report issue" on any lease) to let your investor know something needs attention.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {workOrders.map((wo) => {
              const s = statusMap[wo.status] || statusMap.open;
              return (
                <li key={wo.id} className="p-4 flex items-start gap-3" data-testid={`tenant-wo-${wo.id}`}>
                  <div className="w-9 h-9 bg-slate-900 text-white flex items-center justify-center rounded-sm shrink-0">
                    <Wrench className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900 truncate">{wo.title}</span>
                      <span className={`text-[10px] font-mono uppercase tracking-widest border rounded-sm px-1.5 py-0.5 ${s.cls}`}>
                        {s.label}
                      </span>
                      <span className={`text-[10px] font-mono uppercase tracking-widest ${priorityClr[wo.priority] || "text-slate-500"}`}>
                        · {wo.priority} priority
                      </span>
                    </div>
                    {wo.description && (
                      <div className="text-sm text-slate-600 mt-1 line-clamp-2">{wo.description}</div>
                    )}
                    <div className="text-xs text-slate-400 font-mono mt-1">
                      {wo.wo_number} · submitted {fmtUSDate(wo.created_at)}
                      {wo.unit_label ? ` · Unit ${wo.unit_label}` : ""}
                    </div>
                    <div className="mt-2">
                      <WorkOrderPhotos workOrderId={wo.id} size="sm" maxPreview={5} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function ReportIssueDialog({ lease, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", priority: "medium" });
  // Each photo is wrapped as {id, file} — the id is a stable React key so
  // removing a middle row doesn't shift preview tiles to the wrong File, and
  // keeping the File under `.file` ensures URL.createObjectURL() always gets
  // a real Blob (a prior bug let a bare null/undefined slip into state and
  // crashed the dialog with "Failed to execute 'createObjectURL'").
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // {step: 'wo' | 'photos', current, total}

  const MAX_PHOTOS = 5;
  const _uid = () => (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `ph-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const onAddPhotos = (files) => {
    if (!files || files.length === 0) return;
    const next = [...photos];
    for (const f of Array.from(files)) {
      if (next.length >= MAX_PHOTOS) break;
      // Defensive: reject anything that isn't a real File/Blob or doesn't
      // look like an image. Prevents the dialog from crashing if the OS
      // picker or Playwright returns a weird handle.
      if (!(f instanceof File) && !(f instanceof Blob)) continue;
      if (!f.type || !f.type.startsWith("image/")) continue;
      if (f.size === 0) {
        toast.error(`${f.name || "attachment"} looks empty — skipped.`);
        continue;
      }
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`${f.name || "attachment"} is over 10MB — skipped.`);
        continue;
      }
      next.push({ id: _uid(), file: f });
    }
    setPhotos(next);
  };
  const removePhoto = (id) => setPhotos(photos.filter((p) => p.id !== id));

  // Safely derive preview URLs. If something in state is mangled, render a
  // placeholder instead of crashing the whole dialog.
  const previewUrl = (file) => {
    try {
      if (!(file instanceof File) && !(file instanceof Blob)) return null;
      return URL.createObjectURL(file);
    } catch (_e) {
      return null;
    }
  };

  // Revoke any in-flight object URLs when the dialog unmounts, to free memory.
  useEffect(() => {
    return () => {
      photos.forEach((p) => {
        try { if (p._previewUrl) URL.revokeObjectURL(p._previewUrl); } catch (_e) { /* ignore */ }
      });
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Please enter a short title.");
      return;
    }
    setBusy(true);
    try {
      setProgress({ step: "wo", current: 0, total: 1 });
      const { data: wo } = await api.post("/tenant/maintenance", {
        lease_id: lease.id,
        title: form.title.trim(),
        description: form.description.trim(),
        priority: form.priority,
      });
      // Upload photos one by one so a single failure doesn't kill the rest.
      for (let i = 0; i < photos.length; i++) {
        setProgress({ step: "photos", current: i + 1, total: photos.length });
        const fd = new FormData();
        fd.append("file", photos[i].file);
        fd.append("caption", "");
        try {
          await api.post(`/maintenance/${wo.id}/photos`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) {
          console.error(`Photo ${i + 1} failed`, err);
          toast.error(`Photo ${i + 1} (${photos[i].file.name}) failed to upload — others continued.`);
        }
      }
      toast.success(
        photos.length > 0
          ? `Reported with ${photos.length} ${photos.length === 1 ? "photo" : "photos"}. Your investor has been notified.`
          : "Reported. Your investor has been notified."
      );
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't submit the request.");
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="report-issue-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>Report a maintenance issue</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
            For: <b className="text-slate-900">{lease.property?.name}</b>{lease.unit_label ? ` · Unit ${lease.unit_label}` : ""}
          </div>
          <label className="block">
            <div className="overline text-slate-600 mb-1">What's wrong?</div>
            <input
              required
              maxLength={200}
              className="input-clean w-full"
              data-testid="report-issue-title"
              placeholder="e.g. Kitchen sink is leaking"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </label>
          <label className="block">
            <div className="overline text-slate-600 mb-1">Describe the issue</div>
            <textarea
              rows={4}
              maxLength={4000}
              className="input-clean w-full text-sm"
              data-testid="report-issue-description"
              placeholder="Where is it located, when did it start, how bad is it?"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </label>
          <label className="block">
            <div className="overline text-slate-600 mb-1">Priority</div>
            <select
              className="input-clean w-full"
              data-testid="report-issue-priority"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
            >
              <option value="low">Low — can wait a few days</option>
              <option value="medium">Medium — needs attention this week</option>
              <option value="high">High — urgent / safety</option>
              <option value="emergency">🚨 Emergency — immediate danger (fire, flood, gas leak, no heat in winter)</option>
            </select>
          </label>
          {form.priority === "emergency" ? (
            <div className="text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded-sm px-3 py-2 -mt-2" data-testid="emergency-warning">
              <strong>Emergency selected.</strong> Your landlord's on-call contact will be SMS'd the moment you submit this request. Please only use for genuine emergencies (fire, flood, gas leak, security/structural risk, no heat in winter).
            </div>
          ) : null}

          {/* PHOTOS */}
          <div>
            <div className="overline text-slate-600 mb-1 flex items-center gap-2">
              <Camera className="w-3.5 h-3.5" /> Photos <span className="text-slate-400 font-normal normal-case tracking-normal">({photos.length}/{MAX_PHOTOS}, optional)</span>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {photos.map((p, i) => {
                const url = previewUrl(p.file);
                return (
                  <div key={p.id} className="relative aspect-square rounded-sm overflow-hidden border border-slate-200 bg-slate-50" data-testid={`report-issue-photo-${i}`}>
                    {url ? (
                      <img src={url} alt={p.file?.name || `photo ${i + 1}`} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[10px] text-slate-400 text-center p-1">
                        Preview unavailable
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(p.id)}
                      className="absolute top-1 right-1 w-5 h-5 bg-black/60 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      aria-label={`Remove photo ${i + 1}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
              {photos.length < MAX_PHOTOS && (
                <label
                  className="aspect-square rounded-sm border border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-slate-500 hover:bg-slate-50 text-slate-400"
                  data-testid="report-issue-add-photo"
                >
                  <Camera className="w-5 h-5" />
                  <span className="text-[10px] font-mono uppercase tracking-widest">Add</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => onAddPhotos(e.target.files)}
                  />
                </label>
              )}
            </div>
            <div className="text-[10px] text-slate-400 mt-1">Up to {MAX_PHOTOS} photos, max 10MB each. JPEG/PNG/WebP/HEIC.</div>
          </div>

          {progress && (
            <div className="text-xs text-slate-500 font-mono bg-slate-50 border border-slate-200 rounded-sm px-3 py-2" data-testid="report-issue-progress">
              {progress.step === "wo" ? "Creating request…" : `Uploading photo ${progress.current}/${progress.total}…`}
            </div>
          )}

          <DialogFooter>
            <button type="button" className="btn-outline-dark" onClick={onClose} disabled={busy}>Cancel</button>
            <button
              type="submit"
              disabled={busy}
              className="btn-primary-dark flex items-center gap-2"
              data-testid="report-issue-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Submit request
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- DOCUMENT FOLDERS ----------
// Displays the tenant's documents grouped by category in a friendly folder
// layout. Folders auto-expand when there's only one, otherwise the user can
// click to expand/collapse. Categories without docs are hidden.

const TENANT_UPLOAD_CATEGORIES = [
  { value: "insurance", label: "Renter's insurance" },
  { value: "id", label: "ID / Verification" },
  { value: "correspondence", label: "Correspondence" },
  { value: "general", label: "General" },
  { value: "other", label: "Other" },
];

function TenantUploadButton({ onUploaded }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState("");
  const [category, setCategory] = useState("insurance");

  const reset = () => {
    setFile(null);
    setLabel("");
    setCategory("insurance");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Pick a file first");
    if (file.size > 15 * 1024 * 1024) return toast.error("Max file size is 15 MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label || file.name);
      fd.append("category", category);
      await api.post("/tenant/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      reset();
      setOpen(false);
      if (onUploaded) await onUploaded();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="tenant-upload-doc-cta"
        className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-2 rounded-sm"
      >
        <Plus className="w-3.5 h-3.5" /> Upload document
      </button>
      <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) setOpen(false); }}>
        <DialogContent className="max-w-md" data-testid="tenant-upload-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-slate-700" /> Upload a document
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <label className="block">
              <div className="overline text-slate-600 mb-1">Document type</div>
              <select
                className="input-clean w-full"
                data-testid="tenant-upload-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {TENANT_UPLOAD_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <div className="overline text-slate-600 mb-1">Label (optional)</div>
              <input
                type="text"
                maxLength={120}
                className="input-clean w-full"
                data-testid="tenant-upload-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Renter's insurance 2026"
              />
            </label>
            <label className="block">
              <div className="overline text-slate-600 mb-1">File</div>
              <input
                type="file"
                required
                className="block w-full text-sm text-slate-600"
                data-testid="tenant-upload-file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              {file && (
                <div className="text-xs text-slate-500 font-mono mt-1">
                  {file.name} · {(file.size / 1024).toFixed(0)} KB
                </div>
              )}
            </label>
            <DialogFooter>
              <button type="button" className="btn-outline-dark" onClick={() => { reset(); setOpen(false); }} disabled={busy}>
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy || !file}
                data-testid="tenant-upload-submit"
                className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2 rounded-sm disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Upload
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

const FOLDER_META = {
  lease:          { label: "Lease",            icon: FileSignature, accent: "text-violet-700",   bg: "bg-violet-50",   ring: "ring-violet-200" },
  insurance:      { label: "Insurance",        icon: Shield,        accent: "text-sky-700",      bg: "bg-sky-50",      ring: "ring-sky-200" },
  receipt:        { label: "Receipts",         icon: Receipt,       accent: "text-emerald-700",  bg: "bg-emerald-50",  ring: "ring-emerald-200" },
  id:             { label: "ID & Verification", icon: IdCard,       accent: "text-amber-700",    bg: "bg-amber-50",    ring: "ring-amber-200" },
  w9:             { label: "Tax Forms",        icon: FileSpreadsheet, accent: "text-indigo-700", bg: "bg-indigo-50",   ring: "ring-indigo-200" },
  correspondence: { label: "Correspondence",   icon: Mail,          accent: "text-cyan-700",     bg: "bg-cyan-50",     ring: "ring-cyan-200" },
  general:        { label: "General",          icon: Folder,        accent: "text-slate-700",    bg: "bg-slate-50",    ring: "ring-slate-200" },
  other:          { label: "Other",            icon: Folder,        accent: "text-slate-700",    bg: "bg-slate-50",    ring: "ring-slate-200" },
};

const FOLDER_ORDER = ["lease", "insurance", "receipt", "id", "w9", "correspondence", "general", "other"];

function _folderMeta(category) {
  return FOLDER_META[category] || { label: category || "Other", icon: Folder, accent: "text-slate-700", bg: "bg-slate-50", ring: "ring-slate-200" };
}

function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DocumentFolders({ docs, onDownload }) {
  const grouped = docs.reduce((acc, d) => {
    const cat = (d.category || "general").toLowerCase();
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(d);
    return acc;
  }, {});
  const presentCategories = Object.keys(grouped);
  // Sort: known FOLDER_ORDER first, then any unknown categories alphabetically.
  const orderedCategories = [
    ...FOLDER_ORDER.filter((c) => grouped[c]),
    ...presentCategories.filter((c) => !FOLDER_ORDER.includes(c)).sort(),
  ];

  // Auto-expand all folders when there's only one or two; otherwise collapse all
  // by default so the layout stays compact for tenants with many docs.
  const initialOpen = orderedCategories.length <= 2
    ? Object.fromEntries(orderedCategories.map((c) => [c, true]))
    : {};
  const [open, setOpen] = useState(initialOpen);
  const toggle = (c) => setOpen((p) => ({ ...p, [c]: !p[c] }));

  if (docs.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-10 text-center text-sm text-slate-400" data-testid="tenant-documents-empty">
        Your property manager hasn't shared any documents yet.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="tenant-documents-folders">
      {orderedCategories.map((cat) => {
        const meta = _folderMeta(cat);
        const items = grouped[cat];
        const isOpen = open[cat] !== false && (open[cat] === true || orderedCategories.length <= 2);
        const Icon = meta.icon;
        return (
          <div
            key={cat}
            className={`bg-white border border-slate-200 rounded-sm overflow-hidden ${isOpen ? `ring-1 ${meta.ring}` : ""}`}
            data-testid={`folder-${cat}`}
          >
            <button
              type="button"
              onClick={() => toggle(cat)}
              data-testid={`folder-toggle-${cat}`}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
            >
              <div className={`w-9 h-9 ${meta.bg} ${meta.accent} flex items-center justify-center rounded-sm shrink-0`}>
                {isOpen ? <FolderOpen className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 text-sm">{meta.label}</div>
                <div className="text-xs text-slate-500">
                  {items.length} {items.length === 1 ? "document" : "documents"}
                </div>
              </div>
              <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${isOpen ? "rotate-90" : ""}`} />
            </button>
            {isOpen && (
              <ul className="divide-y divide-slate-100 border-t border-slate-100" data-testid={`folder-contents-${cat}`}>
                {items.map((d) => (
                  <li key={d.id} className="p-4 flex items-center gap-3 hover:bg-slate-50/60 transition-colors" data-testid={`tenant-doc-${d.id}`}>
                    <div className="w-9 h-9 bg-slate-900 text-white flex items-center justify-center rounded-sm shrink-0">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate text-sm">{d.label || d.original_filename}</div>
                      <div className="text-xs text-slate-500 font-mono mt-0.5 truncate">
                        {d.original_filename}
                        {d.size ? ` · ${formatFileSize(d.size)}` : ""}
                        {d.created_at ? ` · ${fmtUSDate(d.created_at)}` : ""}
                      </div>
                    </div>
                    <button
                      onClick={() => onDownload(d)}
                      data-testid={`download-tenant-doc-${d.id}`}
                      className="btn-outline-dark text-xs flex items-center gap-1 shrink-0"
                    >
                      <Download className="w-3 h-3" /> Download
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
